/* eslint-disable react/prop-types */
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getEmailToolEmailLog } from "apis/controllers/emailTool";
import {
  formatDateTime,
  getResponseMessage,
  getValue,
  isErrorResponse,
  readResponseData,
} from "../mailToolUtils";
import {
  EMAIL_MESSAGE_STATUSES,
  MAIL_TOOL_STOP_WARNING_COLOR,
} from "../mailToolConstants";

const POLL_INTERVAL_MS = 2500;

const isPartiallySentStatus = (status) =>
  String(getValue(status, "abbr", "Abbr") || "")
    .toLowerCase()
    .includes("μερικ");

const getLogIcon = (action) => {
  const key = String(action || "").toLowerCase();

  if (key.includes("delivered") || key.includes("succeeded")) {
    return <CheckCircleOutlineIcon color="success" fontSize="small" />;
  }

  if (key.includes("failed") || key.includes("deleted")) {
    return <ErrorOutlineIcon color="error" fontSize="small" />;
  }

  if (key.includes("stopped")) {
    return <StopCircleIcon color="warning" fontSize="small" />;
  }

  if (key.includes("retry")) {
    return <AutorenewIcon color="warning" fontSize="small" />;
  }

  if (key.includes("processing") || key.includes("started")) {
    return <MailOutlineIcon color="primary" fontSize="small" />;
  }

  return <AccessTimeIcon color="disabled" fontSize="small" />;
};

const normalizeLogItem = (item) => {
  const message = getValue(item, "message", "Message") || {};
  const dataValues =
    getValue(message, "dataValues", "DataValues") ||
    getValue(item, "recipient", "Recipient") ||
    {};

  return {
    id: getValue(item, "id", "Id"),
    action: getValue(item, "action", "Action") || "",
    details:
      getValue(item, "details", "Details") ||
      getValue(message, "dataKey", "DataKey") ||
      "",
    logDate: getValue(item, "logDate", "LogDate"),
    dataValues,
    displayName: getValue(dataValues, "displayName", "DisplayName"),
    email: getValue(dataValues, "to", "To", "email", "Email"),
    retries: getValue(dataValues, "retries", "Retries"),
    nextRetry: getValue(dataValues, "nextRetry", "NextRetry"),
    error: getValue(dataValues, "error", "Error"),
    sent: getValue(dataValues, "sent", "Sent"),
    failed: getValue(dataValues, "failed", "Failed"),
    recipients: getValue(dataValues, "recipients", "Recipients"),
  };
};

const getStatusLabel = (status, fallback) =>
  getValue(status, "abbr", "Abbr") || fallback;

const getStatusChipSx = (status, statusLabel, isPolling) => {
  const backendBackground = getValue(status, "color1", "Color1");
  const backendColor = getValue(status, "color2", "Color2");

  if (backendBackground || backendColor) {
    return {
      backgroundColor: backendBackground || undefined,
      borderColor: backendBackground || undefined,
      color: backendColor || undefined,
    };
  }

  const statusId = Number(getValue(status, "id", "Id"));
  const normalizedLabel = String(statusLabel || "").toLocaleLowerCase("el-GR");

  if (
    statusId === EMAIL_MESSAGE_STATUSES.SENT ||
    normalizedLabel.includes("απεστάλ") ||
    normalizedLabel.includes("εστάλ")
  ) {
    return {
      backgroundColor: "#2e7d32",
      borderColor: "#2e7d32",
      color: "#fff",
    };
  }

  if (
    statusId === EMAIL_MESSAGE_STATUSES.PARTIALLY_SENT ||
    normalizedLabel.includes("μερικ")
  ) {
    return {
      backgroundColor: "#f59e0b",
      borderColor: "#f59e0b",
      color: "#111827",
    };
  }

  if (
    statusId === EMAIL_MESSAGE_STATUSES.PROCESSING ||
    normalizedLabel.includes("επεξεργ") ||
    normalizedLabel.includes("αποστολή σε εξέλιξη")
  ) {
    return {
      backgroundColor: "#0288d1",
      borderColor: "#0288d1",
      color: "#fff",
    };
  }

  if (
    statusId === EMAIL_MESSAGE_STATUSES.PENDING ||
    normalizedLabel.includes("αναμον") ||
    normalizedLabel.includes("εκκρεμ")
  ) {
    return {
      backgroundColor: "#ed6c02",
      borderColor: "#ed6c02",
      color: "#fff",
    };
  }

  if (
    statusId === EMAIL_MESSAGE_STATUSES.FAILED ||
    normalizedLabel.includes("απέτυχ") ||
    normalizedLabel.includes("αποτυχ")
  ) {
    return {
      backgroundColor: "#d32f2f",
      borderColor: "#d32f2f",
      color: "#fff",
    };
  }

  if (
    statusId === EMAIL_MESSAGE_STATUSES.STOPPED ||
    normalizedLabel.includes("διακόπ") ||
    normalizedLabel.includes("σταματη")
  ) {
    return {
      backgroundColor: MAIL_TOOL_STOP_WARNING_COLOR,
      borderColor: MAIL_TOOL_STOP_WARNING_COLOR,
      color: "#fff",
    };
  }

  if (
    statusId === EMAIL_MESSAGE_STATUSES.DELETED ||
    normalizedLabel.includes("διαγραμ")
  ) {
    return {
      backgroundColor: "#616161",
      borderColor: "#616161",
      color: "#fff",
    };
  }

  if (isPolling) {
    return {
      backgroundColor: "#0288d1",
      borderColor: "#0288d1",
      color: "#fff",
    };
  }

  return {
    backgroundColor: "#f5f5f5",
    borderColor: "#bdbdbd",
    color: "#424242",
  };
};

const MailDeliveryLog = ({
  emailId,
  poll = false,
  onDone,
}) => {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPolling, setIsPolling] = useState(poll);
  const afterIdRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    setLogs([]);
    setStatus(null);
    setLastUpdated(null);
    setError("");
    setLoading(Boolean(emailId));
    setIsPolling(false);
    afterIdRef.current = null;
    doneRef.current = false;
  }, [emailId]);

  useEffect(() => {
    setIsPolling(poll);
    if (poll) doneRef.current = false;
  }, [poll]);

  const statusLabel = useMemo(
    () => getStatusLabel(status, isPolling ? "Αποστολή σε εξέλιξη" : "Ιστορικό αποστολής"),
    [isPolling, status]
  );
  const statusChipSx = useMemo(
    () => getStatusChipSx(status, statusLabel, isPolling),
    [isPolling, status, statusLabel]
  );

  const fetchLog = useCallback(
    async ({ reset = false } = {}) => {
      if (!emailId) return;

      if (reset) setLoading(true);

      try {
        setError("");
        const response = await getEmailToolEmailLog(
          emailId,
          reset ? null : afterIdRef.current
        );

        if (isErrorResponse(response)) {
          setError(getResponseMessage(response));
          setIsPolling(false);
          return;
        }

        const data = readResponseData(response, {});
        const incoming = (getValue(data, "log", "Log") || []).map(normalizeLogItem);
        const nextStatus = getValue(data, "status", "Status") || null;
        const shouldStopPolling =
          Boolean(getValue(data, "stopPolling", "StopPolling")) ||
          isPartiallySentStatus(nextStatus);

        setStatus(nextStatus);
        setLastUpdated(new Date());

        if (incoming.length > 0) {
          setLogs((prev) => {
            const merged = new Map(prev.map((item) => [item.id, item]));
            incoming.forEach((item) => {
              if (item.id !== undefined && item.id !== null) {
                merged.set(item.id, item);
              }
            });

            const nextLogs = Array.from(merged.values()).sort((a, b) => {
              const dateDiff =
                new Date(b.logDate || 0).getTime() -
                new Date(a.logDate || 0).getTime();
              return dateDiff || Number(b.id || 0) - Number(a.id || 0);
            });
            afterIdRef.current = Math.max(
              afterIdRef.current || 0,
              ...nextLogs.map((item) => Number(item.id || 0))
            );
            return nextLogs;
          });
        }

        if (shouldStopPolling && !doneRef.current) {
          doneRef.current = true;
          setIsPolling(false);
          onDone?.();
        }
      } catch (requestError) {
        setError(
          getResponseMessage(
            requestError,
            "Δεν ήταν δυνατή η φόρτωση της προόδου αποστολής"
          )
        );
        setIsPolling(false);
      } finally {
        setLoading(false);
      }
    },
    [emailId, onDone]
  );

  useEffect(() => {
    fetchLog({ reset: true });
  }, [fetchLog]);

  useEffect(() => {
    if (!emailId || !isPolling) return undefined;

    const timer = window.setInterval(() => fetchLog(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [emailId, fetchLog, isPolling]);

  if (!emailId) return null;

  return (
    <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          {isPolling ? (
            <CircularProgress size={20} thickness={5} />
          ) : (
            <MailOutlineIcon color="primary" />
          )}
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Πρόοδος αποστολής
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Τελευταία ενημέρωση: {lastUpdated ? formatDateTime(lastUpdated) : "-"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Chip
            size="small"
            label={statusLabel}
            variant="filled"
            sx={{
              fontWeight: 700,
              ...statusChipSx,
            }}
          />
          {!isPolling && poll && !doneRef.current && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutorenewIcon />}
              onClick={() => setIsPolling(true)}
            >
              Συνέχεια polling
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Φόρτωση προόδου...
          </Typography>
        </Stack>
      ) : (
        <List
          disablePadding
          sx={{
            maxHeight: 270,
            overflow: "auto",
            borderTop: logs.length ? "1px solid" : 0,
            borderColor: "divider",
          }}
        >
          {logs.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Δεν υπάρχουν ακόμα εγγραφές προόδου.
            </Typography>
          )}
          {logs.map((item, index) => (
            <Box key={item.id || `${item.action}-${item.logDate}`}>
              <ListItem alignItems="flex-start" sx={{ px: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36, pt: 0.25 }}>
                  {getLogIcon(item.action)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={0.75}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {item.details || item.action}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(item.logDate)}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                      {(item.displayName || item.email) && (
                        <Typography variant="caption" color="text.secondary">
                          {[item.displayName, item.email].filter(Boolean).join(" - ")}
                        </Typography>
                      )}
                      {(item.retries || item.nextRetry) && (
                        <Typography variant="caption" color="text.secondary">
                          {item.retries ? `Προσπάθειες: ${item.retries}` : ""}
                          {item.retries && item.nextRetry ? " | " : ""}
                          {item.nextRetry ? `Επόμενη προσπάθεια: ${formatDateTime(item.nextRetry)}` : ""}
                        </Typography>
                      )}
                      {(item.sent !== undefined ||
                        item.failed !== undefined ||
                        item.recipients !== undefined) && (
                        <Typography variant="caption" color="text.secondary">
                          {item.recipients !== undefined ? `Παραλήπτες: ${item.recipients}` : ""}
                          {item.sent !== undefined ? ` | Εστάλησαν: ${item.sent}` : ""}
                          {item.failed !== undefined ? ` | Απέτυχαν: ${item.failed}` : ""}
                        </Typography>
                      )}
                      {item.error && (
                        <Typography variant="caption" color="error">
                          {item.error}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              </ListItem>
              {index < logs.length - 1 && <Divider component="li" />}
            </Box>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default MailDeliveryLog;
